/**
 * Query-key roots of every cached `Reservation` in the funnel.
 *
 * There is one per step rather than one shared key so that each step decides
 * for itself when its copy is refetched: the editor fetches the whole trip
 * (`RESERVATION_EAGER`); passenger-info and the summary fetch the same, lighter
 * tree (`STEP_RESERVATION_EAGER`) but refetch on every mount, because the
 * editor mutates the reservation under its own key in between. Payment
 * deliberately shares the summary's key, which is how the gift-card and
 * discount forms refresh both screens at once.
 *
 * Collected here so code that changes the reservation *server-side* without
 * going through a step's own service can refresh all of them without having to
 * know which steps exist. Signing in is the case that matters: `PUT
 * /webshopSession/customerAccount` re-links the reservation to the customer
 * account (`transferActiveReservationToCustomerAccount`), so every cached copy
 * is stale the moment the call returns — including the bonus rows, which are
 * derived from `reservation.customerPersonId` matching the signed-in customer.
 */
export const RESERVATION_QUERY_KEY_ROOTS = [
  'editor-reservation',
  'passenger-info-reservation',
  'booking-summary-reservation',
] as const;

export const EDITOR_RESERVATION_QUERY_KEY = RESERVATION_QUERY_KEY_ROOTS[0];
export const PASSENGER_INFO_RESERVATION_QUERY_KEY = RESERVATION_QUERY_KEY_ROOTS[1];
/** Shared by `/summary` and `/payment` — the two steps read the same eager tree. */
export const BOOKING_SUMMARY_RESERVATION_QUERY_KEY = RESERVATION_QUERY_KEY_ROOTS[2];

/**
 * The eager tree `/passenger-info`, `/summary` and `/payment` all read.
 *
 * `giftCard` (a Reservation column) and `passenger.extraInvoiceItems` are JSON
 * columns that come back with the base model; listing only actual relations
 * here keeps the backend's eager-expression whitelist happy (see
 * `ALLOWED_RESERVATION_EAGER` in `webshop-allowed-eagers.js`).
 *
 * The waypoints are what let a coach leg name itself — without them
 * `describeProductLabels` falls back to the route group, which is every stop on
 * the line. See `describeBusLine` in editor.types.ts.
 *
 * `quota` and the resource's own waypoints date and time the summary's
 * itinerary: the quota is when a ferry or flight leaves (or which night a hotel
 * row is), and the route's last stop is how long after that it lands, and in
 * which zone. See `timeline-schedule.ts`. A hotel's parent step is the city
 * it stands in, which the summary names beside the stay's dates.
 *
 * One constant, but still two query keys (passenger-info, and summary + payment
 * together): merging them would change when one step's write refreshes
 * another's screen. That is a product decision, not a refactor.
 */
export const STEP_RESERVATION_EAGER =
  '[trip.[primaryDestination,phases],passengers.person,customerPerson,reservedProducts.[product.resource.[travelStep.parent,waypoints.location],tripPhase,quota,startWaypoint.location.travelStep,endWaypoint.location.travelStep],cancellationProtection]';

/**
 * Query keys of everything else the funnel caches about *who* is booking.
 *
 * Written out at the call sites they were easy to mistype, and a typo in an
 * invalidation is silent: the write lands, nothing refetches, and the screen
 * keeps the old answer. Signing in and out invalidate both of these, and the
 * session key is also the one `missingWebshopSessionInterceptor`'s retry
 * refreshes.
 */
export const WEBSHOP_SESSION_QUERY_KEY = 'webshopSession';
/** Keyed by person id as well, so signing out and back in as someone else
 *  cannot serve the previous customer's row. */
export const CUSTOMER_PERSON_QUERY_KEY = 'customerPerson';
/** `GET /persons/:id/reservations` — is this customer shared master data? */
export const PERSON_RESERVATION_IDS_QUERY_KEY = 'personReservationIds';
/** The same question asked once for a whole reservation's passengers. */
export const PASSENGER_RELATED_RESERVATIONS_QUERY_KEY = 'passengerRelatedReservations';
